import React from "react";

import { faBackspace, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Grid } from "@material-ui/core";
import { Field, getIn, setIn } from "formik";

import { Warning } from "components/ErrorMessage";
import {
  withArrayNamespace,
  withNamespace,
} from "components/Form/FormikControls";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

import { ValueSource } from "constants/ruleBooks";

import {
  CheckComparator,
  CheckComparatorDisplayNameMap,
} from "containers/Settings/RuleBooks/constants";
import { DataSource } from "containers/Settings/RuleBooks/DataSource";
import {
  getInputPropsForPropertyType,
  getPropertyType,
  PropertyType,
  PropertyTypeCheckComparatorMap,
} from "containers/Settings/RuleBooks/lib";
import {
  getDataTypeByPath,
  getPropertyByPath,
} from "containers/Settings/RuleBooks/Schema/schema";
import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";

import { useFieldState } from "hooks";

export function Check(props) {
  const {
    criteriaIndex,
    criteriaOptions,
    namespace: ns,
    checkFieldPath,
    parentInputPath,
    showPrerequisiteRuleSource,
    readOnly,
  } = props;

  const schema = useRuleFieldSchema();

  const [value, setValue] = useFieldState(ns);

  const valuesFieldName = withNamespace(ns, "values");
  const comparatorFieldName = withNamespace(ns, "comparator");
  const values = getIn(value, "values");

  const query = getIn(value, "field");

  const dataType = getDataTypeByPath(schema, checkFieldPath);

  const property = getPropertyByPath(dataType, query?.value);

  const valuePresets = property?.set;

  // If we have value presets, and we have values that are not in that list show a warning.
  // Note, that this is only an end user issue if you are changing the fields.  The values are still stored and submitted.
  const valuePresetValues = valuePresets?.map(valuePreset => valuePreset[1]);
  const valuesNotInPresets = valuePresetValues
    ? values.filter(value => !valuePresetValues.includes(value))
    : null;
  const warning =
    valuesNotInPresets && valuesNotInPresets.length > 0
      ? "There are values in your criteria that are no longer available for selection."
      : null;

  function onClickAddAnotherValue() {
    setValue(setIn(value, "values", [...values, ""]));
  }

  function removeValueIndex(index) {
    const nextValues = values.slice();
    nextValues.splice(index, 1);
    setValue(setIn(value, "values", nextValues));
  }

  function onAfterFieldChanged(field) {
    const compatibleSources = [
      ValueSource.FIELD,
      ValueSource.RULESET_FIELD,
      ValueSource.DEPENDENCY_VALUE,
    ];

    // Keep the users fields when changing between parent datatypes, otherwise clear them
    // This is especially useful during the migration period when the rules will be having
    // new parents (a Prerequisite Rule, or a Rule Set) attached to them
    if (
      !compatibleSources.includes(value?.field.source) ||
      !compatibleSources.includes(field.source)
    ) {
      setValue({ ...value, field, comparator: "", values: [""] });
    }
  }

  const propertyType =
    query && query.source === ValueSource.FIELD
      ? getPropertyType(query)
      : // Give the users as much precision as possible for sum and count
        PropertyType.CURRENCY;

  return (
    <>
      {!readOnly && warning ? (
        <Grid item xs={12}>
          <Warning>{warning}</Warning>
        </Grid>
      ) : null}
      <Grid item>
        <span>Data Source: </span>
        <DataSource
          allowAggregateFromIndex={criteriaIndex}
          criteriaOptions={criteriaOptions}
          namespace={withNamespace(ns, "field")}
          onAfterChanged={onAfterFieldChanged}
          showProperties
          showRelations
          fieldPath={checkFieldPath}
          parentInputPath={parentInputPath}
          showPrerequisiteRuleSource={showPrerequisiteRuleSource}
          readOnly={readOnly}
        />
      </Grid>
      <Grid item>
        {!readOnly && <span>Comparator: </span>}
        <Field
          as="select"
          className="p-2"
          name={comparatorFieldName}
          required
          disabled={readOnly}
        >
          <option disabled value="">
            Select a Comparator
          </option>
          {PropertyTypeCheckComparatorMap[propertyType].map((comparator, i) => (
            <option key={i} value={comparator}>
              {CheckComparatorDisplayNameMap[comparator]}
            </option>
          ))}
        </Field>
      </Grid>
      <Grid item>
        {!readOnly && <span>Compare To: </span>}

        {values.map((_ignored, index, array) => {
          function onClickRemoveValueIndex() {
            removeValueIndex(index);
          }
          const isEqualityBasedComparator = [
            CheckComparator.EQUALS,
            CheckComparator.NOT_EQUAL,
          ].includes(value.comparator);

          return (
            <React.Fragment key={index}>
              {Array.isArray(valuePresets) && isEqualityBasedComparator ? (
                <Field
                  as="select"
                  className="p-2"
                  name={withArrayNamespace(valuesFieldName, index)}
                  placeholder="(value)"
                  required
                  disabled={readOnly}
                  {...getInputPropsForPropertyType(propertyType)}
                >
                  <option disabled value="">
                    Select a Value
                  </option>
                  {valuePresets.map(([name, value], index) => (
                    <option value={value} key={index}>
                      {name}
                    </option>
                  ))}
                </Field>
              ) : (
                <Field
                  className="p-2"
                  name={withArrayNamespace(valuesFieldName, index)}
                  placeholder="(value)"
                  required
                  {...getInputPropsForPropertyType(propertyType)}
                  disabled={readOnly}
                />
              )}

              {!readOnly && index > 0 && (
                <Tooltip title="Remove this value">
                  <IconButton onClick={onClickRemoveValueIndex}>
                    <FontAwesomeIcon icon={faBackspace} size="xs" />
                  </IconButton>
                </Tooltip>
              )}
              {index < array.length - 1 && <span className="p-2"> or </span>}
              {!readOnly && index === array.length - 1 && (
                <IconButton onClick={onClickAddAnotherValue}>
                  <FontAwesomeIcon icon={faPlus} size="xs" /> value
                </IconButton>
              )}
            </React.Fragment>
          );
        })}
      </Grid>
    </>
  );
}
